import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Modal,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppSelector } from '@/store';
import { LINKED_ACCOUNTS_COLORS } from '@/constants/residents/mobile/linkedAccountsColors';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { SVG_COMPONENTS } from '@/assets/admin';
import { newInvitationStrings, formatString } from '@/constants/residents/strings';
import { InfoBubbleModal } from './InfoBubbleModal';

const CloseIcon = SVG_COMPONENTS.CANCEL_X;
const MailIcon = SVG_COMPONENTS.EMAIL_SOLID;
const InfoIcon = RESIDENTS_SVG_COMPONENTS.INFORMATION_CIRCLE;
const WarningIcon = RESIDENTS_SVG_COMPONENTS.WARNING;

// Validation constants
const MAX_WORDS = 300;
// Special characters from Figma design: $ # % / < > (shown in orange highlight)
const SPECIAL_CHARS_REGEX = /[<>$#%\/\\]/;

interface NewInvitationModalProps {
  visible: boolean;
  onClose: () => void;
  onSend: (email: string, message: string) => Promise<void>;
}

interface ValidationState {
  wordLimitExceeded: boolean;
  hasSpecialChars: boolean;
  emailInvalid: boolean;
  emailEmpty: boolean;
  showHardErrors: boolean;
}

/**
 * New Invitation Modal
 * Full-screen modal for sending new invitations with validation
 */
export const NewInvitationModal: React.FC<NewInvitationModalProps> = ({
  visible,
  onClose,
  onSend,
}) => {
  const insets = useSafeAreaInsets();
  const { user } = useAppSelector((state) => state.auth);
  
  // Form state
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState('');
  const [showInfoBubble, setShowInfoBubble] = useState(false);
  const [validation, setValidation] = useState<ValidationState>({
    wordLimitExceeded: false,
    hasSpecialChars: false,
    emailInvalid: false,
    emailEmpty: true,
    showHardErrors: false,
  });

  // Generate default message with user's name
  const defaultMessage = useMemo(() => {
    const userName = user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.firstName || user?.email || 'User';
    
    return formatString(newInvitationStrings.messageFormatted, userName);
  }, [user]);

  // Initialize message when modal opens
  useEffect(() => {
    if (visible) {
      setMessage(defaultMessage);
      setEmail('');
      setValidation({
        wordLimitExceeded: false,
        hasSpecialChars: false,
        emailInvalid: false,
        emailEmpty: true,
        showHardErrors: false,
      });
      setIsLoading(false);
      setLoadingEmail('');
      setShowInfoBubble(false);
    }
  }, [visible, defaultMessage]);

  // Count words in message
  const wordCount = useMemo(() => {
    const trimmed = message.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).length;
  }, [message]);

  // Check for special characters in real-time
  const hasSpecialChars = useMemo(() => {
    return SPECIAL_CHARS_REGEX.test(message);
  }, [message]);

  // Check word limit in real-time
  const wordLimitExceeded = useMemo(() => {
    return wordCount > MAX_WORDS;
  }, [wordCount]);

  // Validate email
  const validateEmail = useCallback((text: string) => {
    const trimmed = text.trim();
    const emailEmpty = !trimmed;
    const emailInvalid = !emailEmpty && !trimmed.includes('@');
    
    setValidation(prev => ({
      ...prev,
      emailEmpty,
      emailInvalid,
    }));
    
    return { emailEmpty, emailInvalid };
  }, []);

  // Handle message change
  const handleMessageChange = useCallback((text: string) => {
    setMessage(text);
  }, []);

  // Handle email change
  const handleEmailChange = useCallback((text: string) => {
    setEmail(text);
    validateEmail(text);
  }, [validateEmail]);

  // Determine if message input should show soft error (warning) state
  const showMessageWarning = wordLimitExceeded || hasSpecialChars;

  // Handle send
  const handleSend = useCallback(async () => {
    // Validate all fields
    const emailValidation = validateEmail(email);
    
    const hasErrors = 
      emailValidation.emailEmpty ||
      emailValidation.emailInvalid ||
      wordLimitExceeded ||
      hasSpecialChars;
    
    if (hasErrors) {
      // Show hard errors
      setValidation(prev => ({ ...prev, showHardErrors: true }));
      return;
    }
    
    // Show loading modal
    setLoadingEmail(email.trim());
    setIsLoading(true);
    
    try {
      await onSend(email.trim(), message.trim());
    } finally {
      setIsLoading(false);
      setLoadingEmail('');
    }
  }, [email, message, onSend, validateEmail, wordLimitExceeded, hasSpecialChars]);

  // Build error messages for message field
  const messageErrors = useMemo(() => {
    const errors: string[] = [];
    if (wordLimitExceeded) {
      errors.push(formatString(newInvitationStrings.validation.wordLimitExceeded, MAX_WORDS, wordCount));
    }
    if (hasSpecialChars) {
      errors.push(newInvitationStrings.validation.noSpecialChars);
    }
    return errors;
  }, [wordLimitExceeded, hasSpecialChars, wordCount]);

  // Build error messages for email field
  const emailErrors = useMemo(() => {
    if (validation.showHardErrors) {
      if (validation.emailEmpty) return newInvitationStrings.validation.emailRequired;
      if (validation.emailInvalid) return newInvitationStrings.validation.emailInvalid;
    }
    return '';
  }, [validation.showHardErrors, validation.emailEmpty, validation.emailInvalid]);

  // Memoized warning/error text color based on hard errors state
  const warningTextColor = useMemo(
    () => validation.showHardErrors ? LINKED_ACCOUNTS_COLORS.accentPink : LINKED_ACCOUNTS_COLORS.warningOrange,
    [validation.showHardErrors]
  );

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={onClose}
    >
      <KeyboardAvoidingView
        style={styles.keyboardView}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={0}
      >
        <View style={[styles.container, { paddingTop: insets.top }]}>
          {/* Modal Container with border */}
          <View style={styles.modalContainer}>
            {/* Header - Sticky */}
            <View style={styles.header}>
              <Text style={styles.headerTitle}>{newInvitationStrings.modalTitle}</Text>
              <TouchableOpacity
                style={styles.closeButton}
                onPress={onClose}
                activeOpacity={0.8}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                disabled={isLoading}
              >
                <CloseIcon width={16} height={16} color={LINKED_ACCOUNTS_COLORS.textMuted} />
              </TouchableOpacity>
            </View>

            {/* Scrollable Content */}
            <ScrollView
              style={styles.scrollView}
              contentContainerStyle={styles.scrollContent}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
            >
              {/* Instruction Text */}
              <Text style={styles.instructionText}>
                {newInvitationStrings.header}
              </Text>

              {/* Email Input */}
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>{newInvitationStrings.emailLabel}</Text>
                <View style={[
                  styles.inputContainer,
                  validation.showHardErrors && (validation.emailEmpty || validation.emailInvalid) && styles.inputError,
                ]}>
                  <View style={styles.inputLeft}>
                    <View style={styles.inputIconContainer}>
                      <MailIcon width={20} height={20} color={LINKED_ACCOUNTS_COLORS.textSecondary} />
                    </View>
                    <TextInput
                      style={styles.emailInput}
                      value={email}
                      onChangeText={handleEmailChange}
                      placeholder={newInvitationStrings.emailPlaceholder}
                      placeholderTextColor={LINKED_ACCOUNTS_COLORS.textSecondary}
                      keyboardType="email-address"
                      autoCapitalize="none"
                      autoCorrect={false}
                      autoComplete="email"
                      editable={!isLoading}
                    />
                  </View>
                  <View style={styles.inputRight} />
                </View>
                {emailErrors ? (
                  <Text style={styles.errorText}>{emailErrors}</Text>
                ) : null}
              </View>

              {/* Message Input */}
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>
                  {newInvitationStrings.personalMessageLabel} <Text style={styles.optionalText}>{newInvitationStrings.optionalText}</Text>
                </Text>
                <View style={[
                  styles.textareaContainer,
                  showMessageWarning && !validation.showHardErrors && styles.inputWarningOrange,
                  validation.showHardErrors && showMessageWarning && styles.inputError,
                ]}>
                  <TextInput
                    style={styles.textareaInput}
                    value={message}
                    onChangeText={handleMessageChange}
                    placeholder={newInvitationStrings.messagePlaceholder}
                    placeholderTextColor={LINKED_ACCOUNTS_COLORS.textSecondary}
                    multiline
                    textAlignVertical="top"
                    editable={!isLoading}
                  />
                </View>
                {/* Soft error messages - always show when validation fails */}
                {messageErrors.length > 0 && (
                  <View style={styles.errorContainer}>
                    {messageErrors.map((error, index) => (
                      <View key={index} style={styles.errorRow}>
                        <WarningIcon 
                          width={20} 
                          height={20} 
                          color={warningTextColor} 
                        />
                        <Text style={[styles.warningText, { color: warningTextColor }]}>
                          {error}
                        </Text>
                      </View>
                    ))}
                  </View>
                )}
              </View>

              {/* Info Section */}
              <View style={styles.infoSection}>
                <Text style={styles.disclaimerText}>
                  {newInvitationStrings.disclaimer}
                </Text>

                {/* What information is shared? Link */}
                <TouchableOpacity
                  style={styles.infoLink}
                  onPress={() => setShowInfoBubble(true)}
                  activeOpacity={0.7}
                  disabled={isLoading}
                >
                  <Text style={styles.infoLinkText}>{newInvitationStrings.howItWorks.item2.title}</Text>
                  <InfoIcon width={20} height={20} color={LINKED_ACCOUNTS_COLORS.textPrimary} />
                </TouchableOpacity>
              </View>
            </ScrollView>

            {/* Footer - Sticky */}
            <View style={[styles.footer, { paddingBottom: Math.max(insets.bottom, 24) }]}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={onClose}
                activeOpacity={0.8}
                disabled={isLoading}
              >
                <Text style={styles.cancelButtonText}>{newInvitationStrings.cancelButton}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.sendButton}
                onPress={handleSend}
                activeOpacity={0.8}
                disabled={isLoading}
              >
                <Text style={styles.sendButtonText}>{newInvitationStrings.sendButton}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {/* Loading Modal - Fullscreen centered like confirmation modal */}
        {isLoading && (
          <View style={styles.loadingOverlay}>
            <View style={[styles.loadingModal, { paddingTop: insets.top }]}>
              <View style={styles.loadingContent}>
                <ActivityIndicator size="large" color={LINKED_ACCOUNTS_COLORS.textPrimary} />
                <Text style={styles.loadingText}>
                  {formatString(newInvitationStrings.loadingText, loadingEmail)}
                </Text>
              </View>
            </View>
          </View>
        )}

        {/* Info Bubble Modal */}
        <InfoBubbleModal
          visible={showInfoBubble}
          onClose={() => setShowInfoBubble(false)}
        />
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  keyboardView: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: LINKED_ACCOUNTS_COLORS.background,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: LINKED_ACCOUNTS_COLORS.cardBackground,
    borderWidth: 1,
    borderColor: MARKETPLACE_COLORS.modalBorder,
    borderRadius: 8,
    margin: 1,
    overflow: 'hidden',
  },
  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 32,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: LINKED_ACCOUNTS_COLORS.divider,
  },
  headerTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    lineHeight: 27,
  },
  closeButton: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Scroll content
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
    paddingBottom: 0,
    gap: 16,
  },
  instructionText: {
    fontSize: 16,
    fontWeight: '400',
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    lineHeight: 24,
  },
  // Input groups
  inputGroup: {
    gap: 4,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    lineHeight: 21,
  },
  optionalText: {
    color: LINKED_ACCOUNTS_COLORS.textSecondary,
  },
  inputContainer: {
    flexDirection: 'row',
    backgroundColor: MARKETPLACE_COLORS.inputBackground,
    borderWidth: 1,
    borderColor: MARKETPLACE_COLORS.inputBorder,
    borderRadius: 8,
    overflow: 'hidden',
  },
  inputLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 8,
  },
  inputRight: {
    width: 52,
    borderLeftWidth: 1,
    borderLeftColor: MARKETPLACE_COLORS.inputBorder,
  },
  inputIconContainer: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emailInput: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    padding: 0,
    lineHeight: 17.5,
  },
  textareaContainer: {
    backgroundColor: MARKETPLACE_COLORS.inputBackground,
    borderWidth: 1,
    borderColor: MARKETPLACE_COLORS.inputBorder,
    borderRadius: 8,
    minHeight: 180,
    overflow: 'hidden',
  },
  textareaInput: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    padding: 16,
    paddingTop: 11,
    lineHeight: 17.5,
  },
  inputWarningOrange: {
    borderColor: LINKED_ACCOUNTS_COLORS.warningOrange,
  },
  inputError: {
    borderColor: LINKED_ACCOUNTS_COLORS.accentPink,
  },
  errorContainer: {
    gap: 4,
    paddingTop: 4,
  },
  errorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  warningText: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 21,
  },
  errorText: {
    fontSize: 12,
    fontWeight: '400',
    color: LINKED_ACCOUNTS_COLORS.accentPink,
    lineHeight: 18,
  },
  // Info section
  infoSection: {
    gap: 16,
    paddingBottom: 24,
  },
  disclaimerText: {
    fontSize: 12,
    fontWeight: '300',
    color: LINKED_ACCOUNTS_COLORS.textSecondary,
    lineHeight: 18,
  },
  infoLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  infoLinkText: {
    fontSize: 14,
    fontWeight: '500',
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    lineHeight: 21,
  },
  // Footer
  footer: {
    flexDirection: 'row',
    gap: 20,
    paddingHorizontal: 24,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: LINKED_ACCOUNTS_COLORS.divider,
  },
  cancelButton: {
    flex: 1,
    height: 40,
    borderWidth: 1,
    borderColor: LINKED_ACCOUNTS_COLORS.textPrimary,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    lineHeight: 21,
  },
  sendButton: {
    flex: 1,
    height: 40,
    backgroundColor: LINKED_ACCOUNTS_COLORS.accentBlue,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    lineHeight: 21,
  },
  // Loading Modal - Fullscreen centered
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: LINKED_ACCOUNTS_COLORS.background,
  },
  loadingModal: {
    flex: 1,
    backgroundColor: LINKED_ACCOUNTS_COLORS.cardBackground,
    borderWidth: 1,
    borderColor: MARKETPLACE_COLORS.modalBorder,
    borderRadius: 8,
    margin: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingContent: {
    paddingHorizontal: 32,
    paddingVertical: 40,
    alignItems: 'center',
    gap: 16,
  },
  loadingText: {
    fontSize: 16,
    fontWeight: '400',
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    lineHeight: 24,
    textAlign: 'center',
  },
});
